import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { AppRoute } from "../../../../navigation/routes";
import ClinicianAvailability from "../../clinicians/ClinicianAvailability/ClinicianAvailability";
import queryString from "query-string";
import { AclContext } from "../../../../contexts/AclContext";
import store from "../../../../store";
import { useToasts } from "react-toast-notifications";
import { Button } from "../../bits/Button/Button";
import ChevronBackIcon from "../../icons/ChevronBack";
import ChevronRightIcon from "../../icons/ChevronRight";

interface AvailabilityLocationState {
    clinicianEmail?: string;
}

export default function AvailabilityWrapper() {
    const location = useLocation<AvailabilityLocationState | undefined>();
    const history = useHistory();
    const aclContext = React.useContext(AclContext);
    const { addToast } = useToasts();
    const clinicianEmail = location.state?.clinicianEmail;
    const parsedQueryParams = queryString.parse(location.search || "");
    const delegateEmailAddress = (parsedQueryParams["as"] ?? "") as string;
    const canNavigateClinicians = Boolean(
        aclContext?.authUser.acl?.super_admin &&
            clinicianEmail &&
            !delegateEmailAddress &&
            location.pathname.endsWith(AppRoute.INTAKE_CLINCIAN_AVAILABILITY)
    );
    const [clinicians, setClinicians] = React.useState<
        { id: string; first_name: string; last_name: string }[]
    >([]);

    React.useEffect(() => {
        if (!canNavigateClinicians) {
            return;
        }
        let cancelled = false;
        store.fetchAllCliniciansIdentifier().then(
            (identifiers) => {
                if (!cancelled) {
                    setClinicians(
                        identifiers.sort(
                            (a, b) =>
                                a.first_name.localeCompare(b.first_name) ||
                                a.last_name.localeCompare(b.last_name) ||
                                a.id.localeCompare(b.id)
                        )
                    );
                }
            },
            () => {
                if (!cancelled) {
                    addToast(
                        "Unable to load clinician navigation. Please try again.",
                        {
                            appearance: "error",
                        }
                    );
                }
            }
        );
        return () => {
            cancelled = true;
        };
    }, [canNavigateClinicians, addToast]);

    const clinicianIndex = clinicians.findIndex((c) => c.id === clinicianEmail);
    const previousClinician = clinicians[clinicianIndex - 1];
    const nextClinician =
        clinicianIndex >= 0 ? clinicians[clinicianIndex + 1] : undefined;
    const navigateToClinician = (email: string) => {
        history.replace({
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            state: { ...location.state, clinicianEmail: email },
        });
    };

    if (location.pathname.endsWith(AppRoute.OFFICE_WINDOWS)) {
        return <div>Office Availability</div>;
    } else {
        return (
            <ClinicianAvailability
                key={clinicianEmail}
                delegateUserEmail={delegateEmailAddress}
                loggedInUserEmailProp={clinicianEmail}
                headerActions={
                    canNavigateClinicians ? (
                        <>
                            <Button
                                variant="normal"
                                size="icon"
                                aria-label="Previous clinician"
                                title="Previous clinician"
                                disabled={!previousClinician}
                                onClick={
                                    previousClinician
                                        ? () =>
                                              navigateToClinician(
                                                  previousClinician.id
                                              )
                                        : undefined
                                }
                            >
                                <ChevronBackIcon />
                            </Button>
                            <Button
                                variant="normal"
                                size="icon"
                                aria-label="Next clinician"
                                title="Next clinician"
                                disabled={!nextClinician}
                                onClick={
                                    nextClinician
                                        ? () => navigateToClinician(nextClinician.id)
                                        : undefined
                                }
                            >
                                <ChevronRightIcon />
                            </Button>
                        </>
                    ) : undefined
                }
            />
        );
    }
}
